import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useLoaderData, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import {
  createUploadedFileObjectUrl,
  openUploadedFile,
} from "../../../../../infrastructure/utils/uploadedFileAccess";

import DefaultApiService from "../../../../../infrastructure/api/default";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ReferToDistributionPanel from "../../shared/distribution/ReferToDistributionPanel";
import DropdownPicker from "../../../../components/shared/dropdown";
import JalaliDatePicker from "../../../../components/shared/jalali-datetimepicker";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import { toJalaliFullDate, formatLocalDateTimeForApi } from "../../../../middleware/middleware";
import AssignmentCaseInfoPanel, {
  getCaseContactActions,
  getCaseReferenceId,
} from "../project-assignments/AssignmentCaseInfoPanel";
import { SUPERVISOR_WORKSPACE_NAV_ITEMS } from "../supervisorWorkspaceNav";

const SUPERVISOR_PROJECTS_PATH =
  SUPERVISOR_WORKSPACE_NAV_ITEMS.find((x) => x.id === "projects")?.path ||
  "/SLN/Supervisor/RequestList/ProjectAssignments";

const hasValidVisitDate = (value) => {
  if (!value) return false;
  if (typeof value === "string" && value.startsWith("0001-01-01")) return false;
  if (value instanceof Date && value.getFullYear() <= 1) return false;
  return true;
};

/** Dropdown values are enum names ("SinglePhase"); API expects 1 / 3. */
const resolvePhaseCountNumber = (value) => {
  if (value == null || value === "") return null;
  if (value === "SinglePhase" || value === 1 || value === "1") return 1;
  if (value === "ThreePhase" || value === 3 || value === "3") return 3;
  const n = Number(value);
  if (n === 1 || n === 3) return n;
  return null;
};

const toPhaseCountFormValue = (value) => {
  const n = resolvePhaseCountNumber(value);
  if (n === 3) return "ThreePhase";
  return "SinglePhase"; // default تک‌فاز
};

const INSTALLATION_LOCATION_OTHER = "Other";
const NETWORK_AERIAL = "Aerial";

const DISTRIBUTION_NETWORK_TYPES = [
  { name: "زمینی", value: "Ground" },
  { name: "هوایی", value: "Aerial" },
];

const AERIAL_NETWORK_SUBTYPES = [
  { name: "خودنگهدار", value: "SelfSupporting" },
  { name: "۵ سیمه", value: "FiveWire" },
  { name: "۳ سیمه", value: "ThreeWire" },
];

const resolveDistributionNetworkType = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number") {
    if (value === 1) return "Ground";
    if (value === 2) return "Aerial";
    return "";
  }
  const s = String(value).trim();
  if (s === "Ground" || s === "Aerial") return s;
  if (s === "1") return "Ground";
  if (s === "2") return "Aerial";
  return "";
};

const toDistributionNetworkTypeNumber = (value) => {
  const key = resolveDistributionNetworkType(value);
  if (key === "Ground") return 1;
  if (key === "Aerial") return 2;
  return null;
};

const resolveAerialNetworkSubtype = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number") {
    if (value === 1) return "SelfSupporting";
    if (value === 2) return "FiveWire";
    if (value === 3) return "ThreeWire";
    return "";
  }
  const s = String(value).trim();
  if (["SelfSupporting", "FiveWire", "ThreeWire"].includes(s)) return s;
  if (s === "1") return "SelfSupporting";
  if (s === "2") return "FiveWire";
  if (s === "3") return "ThreeWire";
  return "";
};

const toAerialNetworkSubtypeNumber = (value) => {
  const key = resolveAerialNetworkSubtype(value);
  if (key === "SelfSupporting") return 1;
  if (key === "FiveWire") return 2;
  if (key === "ThreeWire") return 3;
  return null;
};

const resolveInstallationLocationType = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number") {
    const map = {
      1: "FlatRoof",
      2: "PitchedRoof",
      3: "Ground",
      4: "Canopy",
      9: "Other",
    };
    return map[value] || "";
  }
  const s = String(value).trim();
  const allowed = ["FlatRoof", "PitchedRoof", "Ground", "Canopy", "Other"];
  if (allowed.includes(s)) return s;
  if (s === "1") return "FlatRoof";
  if (s === "2") return "PitchedRoof";
  if (s === "3") return "Ground";
  if (s === "4") return "Canopy";
  if (s === "9") return "Other";
  return "";
};

const toInstallationLocationTypeNumber = (value) => {
  const key = resolveInstallationLocationType(value);
  const map = {
    FlatRoof: 1,
    PitchedRoof: 2,
    Ground: 3,
    Canopy: 4,
    Other: 9,
  };
  return map[key] ?? null;
};

const resolveFormAccess = (data) => {
  const fa = data?.formAccess || data?.FormAccess || {};
  const actorType = fa.actorType ?? fa.ActorType;
  const isOperatorActor =
    actorType === 1 || actorType === "OperatorExpert";
  const canEditAsOperator = Boolean(
    fa.canEditAsOperator ?? fa.CanEditAsOperator
  );
  const canEdit = Boolean(fa.canEdit ?? fa.CanEdit);
  return {
    actorType,
    isOperatorActor,
    canEdit,
    isReadOnly: isOperatorActor ? !canEditAsOperator : Boolean(
      fa.isReadOnly ?? fa.IsReadOnly ?? !canEdit
    ),
    canEditAsOperator,
    lockReason: fa.lockReason || fa.LockReason || "",
    canUploadDocument: Boolean(fa.canUploadDocument ?? fa.CanUploadDocument),
    workflowStateTitleFa:
      fa.workflowStateTitleFa || fa.WorkflowStateTitleFa || "",
    canSetVisitDate: Boolean(fa.canSetVisitDate ?? fa.CanSetVisitDate),
    assignmentStatus: fa.assignmentStatus ?? fa.AssignmentStatus,
  };
};

const normalizeSiteVisitForForm = (raw, options = {}) => {
  const v = raw || {};
  const normalized = { ...v };

  ["proposedCapacity", "amper", "requestedCapacity", "transformerCapacity", "distanceFromTransformer"].forEach((k) => {
    if (normalized[k] === 0) normalized[k] = "";
  });
  normalized.phaseCount = toPhaseCountFormValue(
    normalized.phaseCount ?? normalized.PhaseCount
  );
  normalized.installationLocationType = resolveInstallationLocationType(
    normalized.installationLocationType ?? normalized.InstallationLocationType
  );
  normalized.installationLocationOther =
    normalized.installationLocationOther ??
    normalized.InstallationLocationOther ??
    "";
  normalized.distributionNetworkType = resolveDistributionNetworkType(
    normalized.distributionNetworkType ?? normalized.DistributionNetworkType
  );
  normalized.aerialNetworkSubtype = resolveAerialNetworkSubtype(
    normalized.aerialNetworkSubtype ?? normalized.AerialNetworkSubtype
  );
  if (normalized.distributionNetworkType !== NETWORK_AERIAL) {
    normalized.aerialNetworkSubtype = "";
  }

  const areaFromVisit =
    normalized.installableAreaSquareMeter ??
    normalized.InstallableAreaSquareMeter;
  const areaFromRequest =
    options?.installableAreaSquareMeter ??
    options?.InstallableAreaSquareMeter;
  normalized.installableAreaSquareMeter =
    areaFromVisit != null && areaFromVisit !== ""
      ? areaFromVisit
      : areaFromRequest ?? "";

  normalized.isSmartMeter = Boolean(
    normalized.isSmartMeter ?? normalized.IsSmartMeter
  );
  normalized.isBillIdInApplicantName = Boolean(
    normalized.isBillIdInApplicantName ?? normalized.IsBillIdInApplicantName
  );
  normalized.dcCableLengthMeters =
    normalized.dcCableLengthMeters ?? normalized.DcCableLengthMeters ?? "";
  normalized.acCableToMeterLengthMeters =
    normalized.acCableToMeterLengthMeters ??
    normalized.AcCableToMeterLengthMeters ??
    "";
  normalized.gridConnectionCableLengthMeters =
    normalized.gridConnectionCableLengthMeters ??
    normalized.GridConnectionCableLengthMeters ??
    "";
  normalized.needsSitePreparation = Boolean(
    normalized.needsSitePreparation ?? normalized.NeedsSitePreparation
  );
  normalized.sitePreparationNotes =
    normalized.sitePreparationNotes ?? normalized.SitePreparationNotes ?? "";
  normalized.needsSpecialStructure = Boolean(
    normalized.needsSpecialStructure ?? normalized.NeedsSpecialStructure
  );
  normalized.needsCustomStructureDesign = Boolean(
    normalized.needsCustomStructureDesign ??
      normalized.NeedsCustomStructureDesign
  );
  normalized.structureNotes =
    normalized.structureNotes ?? normalized.StructureNotes ?? "";
  normalized.estimatePhotosJson =
    normalized.estimatePhotosJson ?? normalized.EstimatePhotosJson ?? "";
  normalized.description =
    normalized.description ?? normalized.Description ?? "";
  normalized.attachedFileUrl =
    normalized.attachedFileUrl ?? normalized.AttachedFileUrl ?? "";

  // Backend may serialize enums as numbers or enum names; UI Dropdown expects enum names.
  // Pending («درحال بررسی») must remain selectable so supervisors can revert an outcome.
  const resolveSiteVisitStatusForForm = (value) => {
    if (value == null || value === "") return "Pending";
    if (Array.isArray(value) && value.length === 0) return "Pending";

    if (typeof value === "string") {
      const s = value.trim();
      const lower = s.toLowerCase();
      if (
        lower === "pending" ||
        lower === "0" ||
        lower === "درحال بررسی" ||
        lower === "در حال بررسی"
      ) {
        return "Pending";
      }
      if (s === "Accepted" || lower === "accepted") return "Accepted";
      if (s === "Rejected" || lower === "rejected") return "Rejected";
      if (s === "Failed" || lower === "failed") return "Failed";
      if (s === "Withdrawn" || lower === "withdrawn") return "Withdrawn";
      if (s.includes("انصراف")) return "Withdrawn";
      if (s.includes("تایید نمی") || s.includes("تأیید نمی")) return "Rejected";
      if (s.includes("ناموفق")) return "Failed";
      if (s.includes("تایید") || s.includes("تأیید")) return "Accepted";
      return "Pending";
    }

    if (typeof value === "number") {
      if (value === 1) return "Accepted";
      if (value === 2) return "Rejected";
      if (value === 3) return "Failed";
      if (value === 4) return "Withdrawn";
      return "Pending";
    }

    return "Pending";
  };

  normalized.siteVisitStatus = resolveSiteVisitStatusForForm(
    normalized.siteVisitStatus ?? normalized.SiteVisitStatus
  );

  ["scheduledVisitTime", "actualVisitTime"].forEach((k) => {
    const val = normalized[k];
    if (typeof val === "string" && val.startsWith("0001-01-01")) normalized[k] = "";
  });

  const requestBillId =
    options?.requestBillId ||
    options?.electricBoillNumber ||
    normalized.billId ||
    normalized.BillId ||
    "";
  normalized.billId = requestBillId;

  return normalized;
};

const isWithdrawnStatus = (value) => {
  if (value == null || value === "") return false;
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    return isWithdrawnStatus(value[0]);
  }
  if (typeof value === "number") return value === 4;
  const normalized = String(value).trim().toLowerCase();
  return (
    normalized === "withdrawn" ||
    normalized === "4" ||
    normalized.includes("انصراف")
  );
};

const buildFullVisitValidationShape = () => ({
  actualVisitTime: Yup.mixed().test(
    "required-actual",
    "زمان انجام بازدید الزامی است.",
    (value) => hasValidVisitDate(value)
  ),
  proposedCapacity: Yup.number()
    .typeError("ظرفیت پیشنهادی باید عدد باشد.")
    .moreThan(0, "ظرفیت پیشنهادی پس از بازدید الزامی است."),
  phaseCount: Yup.mixed()
    .required("نوع انشعاب را انتخاب کنید.")
    .test(
      "phase-required",
      "نوع انشعاب را انتخاب کنید (تک‌فاز / سه‌فاز).",
      (value) => resolvePhaseCountNumber(value) != null
    ),
  installationLocationType: Yup.string()
    .required("نوع محل نصب را انتخاب کنید.")
    .oneOf(
      ["FlatRoof", "PitchedRoof", "Ground", "Canopy", "Other"],
      "نوع محل نصب نامعتبر است."
    ),
  installationLocationOther: Yup.string().when("installationLocationType", {
    is: INSTALLATION_LOCATION_OTHER,
    then: () =>
      Yup.string()
        .trim()
        .required("برای گزینه سایر، توضیح محل نصب را وارد کنید.")
        .max(200, "توضیح حداکثر ۲۰۰ کاراکتر است."),
    otherwise: () => Yup.string().nullable(),
  }),
  distributionNetworkType: Yup.string()
    .required("نوع شبکه را انتخاب کنید.")
    .oneOf(["Ground", "Aerial"], "نوع شبکه نامعتبر است."),
  aerialNetworkSubtype: Yup.string().when("distributionNetworkType", {
    is: NETWORK_AERIAL,
    then: () =>
      Yup.string()
        .required("نوع شبکه هوایی را انتخاب کنید.")
        .oneOf(
          ["SelfSupporting", "FiveWire", "ThreeWire"],
          "نوع شبکه هوایی نامعتبر است."
        ),
    otherwise: () => Yup.string().nullable(),
  }),
  installableAreaSquareMeter: Yup.number()
    .typeError("مساحت باید عدد باشد.")
    .moreThan(0, "مساحت قابل نصب را وارد کنید.")
    .required("مساحت قابل نصب را وارد کنید."),
  description: Yup.string()
    .trim()
    .required("توضیحات ناظر را به‌صورت مفصل وارد کنید.")
    .min(20, "توضیحات ناظر باید حداقل ۲۰ کاراکتر باشد."),
  siteVisitStatus: Yup.mixed()
    .required("وضعیت بازدید را انتخاب کنید.")
    .test(
      "status-valid",
      "وضعیت بازدید را مشخص کنید (در حال بررسی / مورد تأیید / عدم تأیید / ناموفق / انصراف).",
      (value) => {
        if (value == null || value === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        const normalized = String(value).trim().toLowerCase();
        return (
          normalized === "pending" ||
          normalized === "0" ||
          normalized === "accepted" ||
          normalized === "1" ||
          normalized === "rejected" ||
          normalized === "2" ||
          normalized === "failed" ||
          normalized === "3" ||
          normalized === "withdrawn" ||
          normalized === "4" ||
          normalized.includes("انصراف")
        );
      }
    ),
});

const buildWithdrawnValidationShape = () => ({
  siteVisitStatus: Yup.mixed().required("وضعیت بازدید را انتخاب کنید."),
  description: Yup.string()
    .trim()
    .required("دلیل انصراف متقاضی را وارد کنید.")
    .min(10, "دلیل انصراف باید حداقل ۱۰ کاراکتر باشد."),
});

const buildValidationSchema = (canEdit) => {
  if (!canEdit) return Yup.object().shape({});

  // Lazy so «انصراف» skips all visit-form fields except status + reason.
  return Yup.lazy((values) =>
    Yup.object().shape(
      isWithdrawnStatus(values?.siteVisitStatus)
        ? buildWithdrawnValidationShape()
        : buildFullVisitValidationShape()
    )
  );
};

const SiteVisitModifyPage = () => {
  const data = useLoaderData("root") || {};
  const siteVisit = data.siteVisit || data.SiteVisit || {};
  const caseInfo = data.caseInfo || data.CaseInfo || null;
  const requestBillId =
    caseInfo?.electricBoillNumber ||
    caseInfo?.ElectricBoillNumber ||
    siteVisit.billId ||
    siteVisit.BillId ||
    "";
  const initialValues = useMemo(
    () =>
      normalizeSiteVisitForForm(siteVisit, {
        requestBillId,
        installableAreaSquareMeter:
          caseInfo?.installableAreaSquareMeter ??
          caseInfo?.InstallableAreaSquareMeter,
      }),
    [siteVisit, requestBillId, caseInfo]
  );
  const formAccess = useMemo(() => resolveFormAccess(data), [data]);
  const phaseCount = data.phaseCount || data.PhaseCount || [];
  const installationLocationTypes =
    data.installationLocationTypes || data.InstallationLocationTypes || [];
  const distributionNetworkTypes =
    data.distributionNetworkTypes ||
    data.DistributionNetworkTypes ||
    DISTRIBUTION_NETWORK_TYPES;
  const aerialNetworkSubtypes =
    data.aerialNetworkSubtypes ||
    data.AerialNetworkSubtypes ||
    AERIAL_NETWORK_SUBTYPES;
  const siteVisitStatus = data.siteVisitStatus || data.SiteVisitStatus || [];
  const baseUrl = data.baseUrl || data.BaseUrl || "";
  const caseReferenceId = getCaseReferenceId(caseInfo);
  const caseTitle =
    caseInfo?.requestTitle ||
    caseInfo?.RequestTitle ||
    (caseReferenceId ? `درخواست ${caseReferenceId}` : "");
  const applicantName = [
    caseInfo?.applicantFirstName || caseInfo?.ApplicantFirstName,
    caseInfo?.applicantLastName || caseInfo?.ApplicantLastName,
  ]
    .filter(Boolean)
    .join(" ");
  const siteVisitTitle =
    caseTitle ||
    siteVisit.requestTitle ||
    siteVisit.RequestTitle ||
    siteVisit.title ||
    siteVisit.Title ||
    "";
  const headerTitleExtra = [siteVisitTitle, applicantName]
    .filter(Boolean)
    .join(" — ");
  const { mobile: applicantMobile, mapUrl: caseMapUrl } =
    getCaseContactActions(caseInfo);
  const scheduledTimeSet = hasValidVisitDate(siteVisit.scheduledVisitTime);
  const scheduledTimeLabel = scheduledTimeSet
    ? toJalaliFullDate(siteVisit.scheduledVisitTime)
    : "هنوز هماهنگ نشده";

  let navigate = useNavigate();
  const location = useLocation();
  const { id: roleId } = useParams();
  const [loading, setLoading] = useState(false);
  const [attachmentObjectUrl, setAttachmentObjectUrl] = useState("");
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const isReadOnly = formAccess.isReadOnly;
  const validationSchema = useMemo(
    () => buildValidationSchema(formAccess.canEdit),
    [formAccess.canEdit]
  );

  const onNavigateToListPage = () => {
    const from = location.state?.from;
    if (
      typeof from === "string" &&
      from.startsWith("/SLN/Supervisor") &&
      !from.includes("/SiteVisit/")
    ) {
      navigate(from);
      return;
    }
    // Primary supervisor inbox is project assignments, not the legacy SiteVisit grid.
    navigate(SUPERVISOR_PROJECTS_PATH);
  };

  const onSubmit = async (formValue, { setSubmitting }) => {
    if (isReadOnly || (formAccess.isOperatorActor && !formAccess.canEditAsOperator)) {
      setSubmitting(false);
      return;
    }

    setLoading(true);
    const resolvedPhaseCount = resolvePhaseCountNumber(
      formValue.phaseCount ?? siteVisit.phaseCount ?? siteVisit.PhaseCount
    ) ?? 1;
    const resolvedPhaseCountName =
      resolvedPhaseCount === 3 ? "ThreePhase" : "SinglePhase";

    const toIntOrZero = (value) => {
      if (value == null || value === "") return 0;
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n : 0;
    };
    const toNumberOrZero = (value) => {
      if (value == null || value === "") return 0;
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n : 0;
    };

    const sendData = {
      ...siteVisit,
      ...formValue,
      id: siteVisit.id || formValue.id,
      billId: requestBillId || formValue.billId || siteVisit.billId || "",
      requestedCapacity: toIntOrZero(
        formValue.requestedCapacity ?? siteVisit.requestedCapacity
      ),
      proposedCapacity: toIntOrZero(
        formValue.proposedCapacity ?? siteVisit.proposedCapacity
      ),
      amper: toIntOrZero(formValue.amper ?? siteVisit.amper),
      transformerCapacity: toNumberOrZero(
        formValue.transformerCapacity ?? siteVisit.transformerCapacity
      ),
      distanceFromTransformer: toNumberOrZero(
        formValue.distanceFromTransformer ?? siteVisit.distanceFromTransformer
      ),

      // Send both casings so ASP.NET JSON binding can't miss it.
      // Send string enum name as well (some enum binding configs accept name, not number).
      phaseCount: resolvedPhaseCountName,
      PhaseCount: resolvedPhaseCount,

      installationLocationType: resolveInstallationLocationType(
        formValue.installationLocationType
      ),
      InstallationLocationType: toInstallationLocationTypeNumber(
        formValue.installationLocationType
      ),
      installationLocationOther:
        resolveInstallationLocationType(formValue.installationLocationType) ===
        INSTALLATION_LOCATION_OTHER
          ? String(formValue.installationLocationOther || "").trim()
          : null,
      InstallationLocationOther:
        resolveInstallationLocationType(formValue.installationLocationType) ===
        INSTALLATION_LOCATION_OTHER
          ? String(formValue.installationLocationOther || "").trim()
          : null,

      distributionNetworkType: resolveDistributionNetworkType(
        formValue.distributionNetworkType
      ),
      DistributionNetworkType: toDistributionNetworkTypeNumber(
        formValue.distributionNetworkType
      ),
      aerialNetworkSubtype:
        resolveDistributionNetworkType(formValue.distributionNetworkType) ===
        NETWORK_AERIAL
          ? resolveAerialNetworkSubtype(formValue.aerialNetworkSubtype)
          : null,
      AerialNetworkSubtype:
        resolveDistributionNetworkType(formValue.distributionNetworkType) ===
        NETWORK_AERIAL
          ? toAerialNetworkSubtypeNumber(formValue.aerialNetworkSubtype)
          : null,
      installableAreaSquareMeter: toNumberOrZero(
        formValue.installableAreaSquareMeter
      ),
      InstallableAreaSquareMeter: toNumberOrZero(
        formValue.installableAreaSquareMeter
      ),
      isSmartMeter: Boolean(formValue.isSmartMeter),
      IsSmartMeter: Boolean(formValue.isSmartMeter),
      isBillIdInApplicantName: Boolean(formValue.isBillIdInApplicantName),
      IsBillIdInApplicantName: Boolean(formValue.isBillIdInApplicantName),
      dcCableLengthMeters: toNumberOrZero(formValue.dcCableLengthMeters) || null,
      DcCableLengthMeters: toNumberOrZero(formValue.dcCableLengthMeters) || null,
      acCableToMeterLengthMeters:
        toNumberOrZero(formValue.acCableToMeterLengthMeters) || null,
      AcCableToMeterLengthMeters:
        toNumberOrZero(formValue.acCableToMeterLengthMeters) || null,
      gridConnectionCableLengthMeters:
        toNumberOrZero(formValue.gridConnectionCableLengthMeters) || null,
      GridConnectionCableLengthMeters:
        toNumberOrZero(formValue.gridConnectionCableLengthMeters) || null,
      needsSitePreparation: Boolean(formValue.needsSitePreparation),
      NeedsSitePreparation: Boolean(formValue.needsSitePreparation),
      sitePreparationNotes: String(formValue.sitePreparationNotes || "").trim(),
      SitePreparationNotes: String(formValue.sitePreparationNotes || "").trim(),
      needsSpecialStructure: Boolean(formValue.needsSpecialStructure),
      NeedsSpecialStructure: Boolean(formValue.needsSpecialStructure),
      needsCustomStructureDesign: Boolean(formValue.needsCustomStructureDesign),
      NeedsCustomStructureDesign: Boolean(formValue.needsCustomStructureDesign),
      structureNotes: String(formValue.structureNotes || "").trim(),
      StructureNotes: String(formValue.structureNotes || "").trim(),
      estimatePhotosJson: String(formValue.estimatePhotosJson || "").trim() || null,
      EstimatePhotosJson:
        String(formValue.estimatePhotosJson || "").trim() || null,
      description: String(formValue.description || "").trim(),
      Description: String(formValue.description || "").trim(),
      attachedFileUrl: formValue.attachedFileUrl || "",
      AttachedFileUrl: formValue.attachedFileUrl || "",

      scheduledVisitTime: formatLocalDateTimeForApi(
        formValue.scheduledVisitTime ?? siteVisit.scheduledVisitTime
      ),
      actualVisitTime: formatLocalDateTimeForApi(
        formValue.actualVisitTime ?? siteVisit.actualVisitTime
      ),
    };

    try {
      const res = await DefaultApiService.modifyApi(
        "SLN/Supervisor/SiteVisit",
        sendData
      );

      if (res.isSuccess) {
        toast.success(res.message);
        onNavigateToListPage();
      } else {
        toast.error(res.message);
      }

      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
      setLoading(false);
      setSubmitting(false);
    }
  };

  const attachedFileUrl =
    siteVisit.attachedFileUrl || siteVisit.AttachedFileUrl || "";

  useEffect(() => {
    let revoked = false;
    let currentUrl = "";

    const loadPreview = async () => {
      const path = attachedFileUrl;
      if (!path || !/\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(path)) {
        setAttachmentObjectUrl("");
        return;
      }
      try {
        const { objectUrl } = await createUploadedFileObjectUrl(path, baseUrl);
        if (revoked) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        currentUrl = objectUrl;
        setAttachmentObjectUrl(objectUrl);
      } catch {
        if (!revoked) setAttachmentObjectUrl("");
      }
    };

    loadPreview();
    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [attachedFileUrl, baseUrl]);

  const handleOpenAttachment = async (path, { download = false } = {}) => {
    if (!path) return;
    setAttachmentBusy(true);
    try {
      await openUploadedFile(path, baseUrl, {
        download,
        fileName: String(path).split("/").pop() || "site-visit-attachment",
      });
    } catch (err) {
      toast.error(err?.message || "باز کردن فایل پیوست ناموفق بود.");
    } finally {
      setAttachmentBusy(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <ReferToDistributionPanel
          requestId={
            siteVisit?.requestId ||
            siteVisit?.RequestId ||
            caseInfo?.requestId ||
            caseInfo?.RequestId
          }
        />
      </Grid>

      {caseInfo ? (
        <Grid item xs={12} md={12}>
          <Card variant="outlined" sx={{ overflow: "visible" }}>
            <CardHeader
              title="اطلاعات پرونده متقاضی"
              subheader="فقط مشاهده — برای ثبت جزئیات بازدید از فرم پایین استفاده کنید"
              sx={{
                py: 1.25,
                px: 2,
                "& .MuiCardHeader-title": { fontSize: "1rem", fontWeight: 700 },
                "& .MuiCardHeader-subheader": { fontSize: "0.75rem", mt: 0.25 },
                "& .MuiCardHeader-action": { m: 0, alignSelf: "center" },
              }}
              action={
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  {caseReferenceId ? (
                    <Chip size="small" label={`پیگیری ${caseReferenceId}`} />
                  ) : null}
                  {applicantMobile ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PhoneIcon />}
                      href={`tel:${applicantMobile}`}
                    >
                      تماس
                    </Button>
                  ) : null}
                  {caseMapUrl ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PlaceIcon />}
                      href={caseMapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      نقشه
                    </Button>
                  ) : null}
                </Stack>
              }
            />
            <CardContent sx={{ pt: 0, px: 2, pb: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Box
                sx={{
                  bgcolor: "grey.50",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <AssignmentCaseInfoPanel item={caseInfo} dense />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ) : null}

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              roleId !== "create"
                ? `ثبت اطلاعات بازدید — ${headerTitleExtra || "بازدید"}`
                : "ایجاد"
            }
            subheader={
              formAccess.canEdit
                ? "پس از هماهنگی زمان بازدید، جزئیات انجام بازدید و گزارش را ثبت کنید."
                : "فرم بازدید در حالت مشاهده است."
            }
            action={
              formAccess.workflowStateTitleFa ? (
                <Chip
                  size="small"
                  color={isReadOnly ? "default" : "primary"}
                  label={formAccess.workflowStateTitleFa}
                />
              ) : null
            }
          />

          <CardContent>
            {isReadOnly && formAccess.lockReason ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {formAccess.lockReason}
              </Alert>
            ) : null}

            {formAccess.canEdit && !scheduledTimeSet ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  زمان بازدید هنوز هماهنگ نشده است.
                  {formAccess.canSetVisitDate
                    ? " ابتدا از لیست بازدید (آیکن تعیین زمان) زمان هماهنگ‌شده را ثبت کنید."
                    : " منتظر تعیین زمان توسط ناظر ارجاع‌شده باشید."}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onNavigateToListPage}
                >
                  بازگشت به لیست بازدید
                </Button>
              </Alert>
            ) : null}

            {scheduledTimeSet ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                زمان هماهنگ‌شده: <strong>{scheduledTimeLabel}</strong>
              </Alert>
            ) : null}

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              enableReinitialize
              validateOnChange
              validateOnBlur
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
                setFieldTouched,
                setErrors,
                setTouched,
                validateForm,
              }) => {
                const withdrawnMode = isWithdrawnStatus(values.siteVisitStatus);
                const onMakeFormikVariable = (name, placeholder = "") => ({
                  name,
                  fullWidth: true,
                  value: values[name] ?? "",
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                  placeholder,
                  disabled: isReadOnly,
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ظرفیت درخواستی"
                          disabled
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("requestedCapacity")}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label={withdrawnMode ? "ظرفیت پیشنهادی" : "ظرفیت پیشنهادی *"}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("proposedCapacity")}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="آمپر"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("amper")}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ظرفیت ترانس (kVA)"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "transformerCapacity",
                            "مثلاً ۲۰۰"
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="فاصله از ترانس (متر)"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "distanceFromTransformer",
                            "مثلاً ۵۰"
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="شناسه قبض"
                          disabled
                          value={requestBillId || values.billId || "—"}
                          helperText="از اطلاعات درخواست (فقط نمایش)"
                          fullWidth
                          variant="outlined"
                          inputProps={{ dir: "ltr" }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <JalaliDatePicker
                          label="تاریخ پیشنهادی بازدید"
                          onChange={() => {}}
                          {...onMakeFormikVariable("scheduledVisitTime")}
                          disabled
                          readOnly
                          helperText="فقط خواندنی — از هماهنگی قبلی"
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label={withdrawnMode ? "نوع انشعاب" : "نوع انشعاب *"}
                          disabled={isReadOnly}
                          onChange={(event) => {
                            setFieldValue("phaseCount", event.target.value);
                          }}
                          list={phaseCount}
                          filterable={false}
                          {...onMakeFormikVariable("phaseCount")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label={withdrawnMode ? "نوع محل نصب پنل" : "نوع محل نصب پنل *"}
                          disabled={isReadOnly}
                          onChange={(event) => {
                            const next = event.target.value;
                            setFieldValue("installationLocationType", next);
                            if (next !== INSTALLATION_LOCATION_OTHER) {
                              setFieldValue("installationLocationOther", "");
                            }
                          }}
                          list={installationLocationTypes}
                          filterable={false}
                          {...onMakeFormikVariable("installationLocationType")}
                        />
                      </Grid>

                      {values.installationLocationType ===
                      INSTALLATION_LOCATION_OTHER ? (
                        <Grid item xs={12} md={6} lg={6}>
                          <TextField
                            label="توضیح محل نصب (سایر) *"
                            onChange={wrapFormikHandleChange(handleChange)}
                            {...onMakeFormikVariable("installationLocationOther")}
                          />
                        </Grid>
                      ) : null}

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label={
                            withdrawnMode
                              ? "مساحت قابل نصب (م²)"
                              : "مساحت قابل نصب (م²) *"
                          }
                          onChange={wrapFormikHandleChange(handleChange)}
                          helperText="از ثبت درخواست متقاضی لود شده؛ ناظر می‌تواند اصلاح کند"
                          {...onMakeFormikVariable("installableAreaSquareMeter")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label={withdrawnMode ? "نوع شبکه" : "نوع شبکه *"}
                          disabled={isReadOnly}
                          onChange={(event) => {
                            const next = event.target.value;
                            setFieldValue("distributionNetworkType", next);
                            if (next !== NETWORK_AERIAL) {
                              setFieldValue("aerialNetworkSubtype", "");
                            }
                          }}
                          list={distributionNetworkTypes}
                          filterable={false}
                          {...onMakeFormikVariable("distributionNetworkType")}
                        />
                      </Grid>

                      {values.distributionNetworkType === NETWORK_AERIAL ? (
                        <Grid item xs={12} md={6} lg={3}>
                          <DropdownPicker
                            label={withdrawnMode ? "نوع شبکه هوایی" : "نوع شبکه هوایی *"}
                            disabled={isReadOnly}
                            onChange={(event) => {
                              setFieldValue(
                                "aerialNetworkSubtype",
                                event.target.value
                              );
                            }}
                            list={aerialNetworkSubtypes}
                            filterable={false}
                            {...onMakeFormikVariable("aerialNetworkSubtype")}
                          />
                        </Grid>
                      ) : null}

                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="isSharedRooftop"
                          value={values.isSharedRooftop}
                          checked={values.isSharedRooftop}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="محل نصب سامانه، پشت‌بام یا فضای مشاع ساختمان است"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="hasMeterLocation"
                          value={values.hasMeterLocation}
                          checked={values.hasMeterLocation}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="محل مناسب جهت نصب کنتور هوشمند موجود می باشد"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="isSmartMeter"
                          value={values.isSmartMeter}
                          checked={Boolean(values.isSmartMeter)}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="کنتور مصرف هوشمند است"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="isBillIdInApplicantName"
                          value={values.isBillIdInApplicantName}
                          checked={Boolean(values.isBillIdInApplicantName)}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="شناسه قبض به نام متقاضی است"
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="isBuildingUnder10Yrs"
                          value={values.isBuildingUnder10Yrs}
                          checked={values.isBuildingUnder10Yrs}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="ساختمان مشترک دارای عمری کمتر از 10 سال می باشد"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="isFullSunlight"
                          value={values.isFullSunlight}
                          checked={values.isFullSunlight}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="محل طی روز آفتاب گیر بوده و هیچ مانعی بر روی آرایه منصوبه سایه ایجاد نمی کند"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="isSubscriptionVerified"
                          value={values.isSubscriptionVerified}
                          checked={values.isSubscriptionVerified}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="مشخصات اشتراک به طور کامل درج و با قبض مشترک مطابقت دارد"
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="isVisitedByDistributionCompanySupervisor"
                          value={values.isVisitedByDistributionCompanySupervisor}
                          checked={Boolean(values.isVisitedByDistributionCompanySupervisor)}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="بازدید توسط ناظر شرکت توزیع انجام شده است (نظارت توسط اینجانب انجام نشده)"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mt: 1 }}>
                          اطلاعات برآورد قیمت (کابل و سازه)
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="طول سیم DC (متر)"
                          disabled={isReadOnly}
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("dcCableLengthMeters")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="طول کابل AC تا تابلوی کنتور (متر)"
                          disabled={isReadOnly}
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("acCableToMeterLengthMeters")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="طول کابل اتصال به شبکه (متر)"
                          disabled={isReadOnly}
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "gridConnectionCableLengthMeters"
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="needsSitePreparation"
                          checked={Boolean(values.needsSitePreparation)}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="نیاز به آماده‌سازی محل نصب"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="needsSpecialStructure"
                          checked={Boolean(values.needsSpecialStructure)}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="سازه خاص"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          disabled={isReadOnly}
                          name="needsCustomStructureDesign"
                          checked={Boolean(values.needsCustomStructureDesign)}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="نیاز به طراحی سازه خاص"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="توضیحات آماده‌سازی محل"
                          disabled={isReadOnly}
                          multiline
                          minRows={2}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("sitePreparationNotes")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="توضیحات سازه"
                          disabled={isReadOnly}
                          multiline
                          minRows={2}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("structureNotes")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label={
                            isWithdrawnStatus(values.siteVisitStatus)
                              ? "دلیل انصراف متقاضی *"
                              : "توضیحات ناظر (مفصل) *"
                          }
                          multiline
                          minRows={5}
                          onChange={wrapFormikHandleChange(handleChange)}
                          helperText={
                            isWithdrawnStatus(values.siteVisitStatus)
                              ? "زمان و نحوه اعلام انصراف و دلیل گفته‌شده توسط متقاضی را بنویسید تا کارشناس بررسی کند"
                              : "وضعیت محل، شبکه، کنتور و نکات بازدید را کامل بنویسید"
                          }
                          {...onMakeFormikVariable("description")}
                        />
                      </Grid>

                      {isWithdrawnStatus(values.siteVisitStatus) ? (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            با ثبت «انصراف متقاضی»، ارجاع بازدید بسته می‌شود و کارشناس
                            می‌تواند پس از بررسی، انصراف را تأیید و پرونده را از چرخه خارج کند.
                            تکمیل سایر فیلدهای فرم بازدید در این حالت الزامی نیست.
                          </Alert>
                        </Grid>
                      ) : null}

                      <Grid item xs={12} md={6} lg={3}>
                        <JalaliDatePicker
                          label={
                            withdrawnMode
                              ? "زمان انجام بازدید"
                              : "زمان انجام بازدید *"
                          }
                          disabled={isReadOnly}
                          onChange={(value) => {
                            setFieldValue("actualVisitTime", value);
                            setFieldTouched("actualVisitTime", true, false);
                          }}
                          onBlur={() =>
                            setFieldTouched("actualVisitTime", true)
                          }
                          {...onMakeFormikVariable("actualVisitTime")}
                          error={Boolean(
                            errors.actualVisitTime && touched.actualVisitTime
                          )}
                          helperText={
                            errors.actualVisitTime && touched.actualVisitTime
                              ? errors.actualVisitTime
                              : withdrawnMode
                                ? "برای انصراف الزامی نیست"
                                : "زمان واقعی انجام بازدید را ثبت کنید"
                          }
                          FormHelperTextProps={{
                            sx: {
                              color:
                                errors.actualVisitTime &&
                                touched.actualVisitTime
                                  ? "error.main"
                                  : undefined,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label="وضعیت *"
                          disabled={isReadOnly}
                          onChange={async (event) => {
                            const next = event.target.value;
                            await setFieldValue("siteVisitStatus", next, false);
                            if (isWithdrawnStatus(next)) {
                              setErrors({});
                              setTouched(
                                {
                                  siteVisitStatus: true,
                                  description: Boolean(touched.description),
                                },
                                false
                              );
                            }
                            await validateForm({
                              ...values,
                              siteVisitStatus: next,
                            });
                          }}
                          list={siteVisitStatus}
                          filterable={false}
                          {...onMakeFormikVariable("siteVisitStatus")}
                        />
                      </Grid>

                      {(() => {
                        const fileUrl =
                          values.attachedFileUrl || attachedFileUrl || "";
                        const isImage = /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(
                          fileUrl
                        );
                        const previewSrc = attachmentObjectUrl || "";
                        return (
                          <>
                            {fileUrl ? (
                              <Grid item xs={12}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mb: 0.75 }}
                                >
                                  پیش‌نمایش فرم اسکن‌شده بازدید
                                </Typography>
                                {isImage && previewSrc ? (
                                  <Box
                                    component="img"
                                    src={previewSrc}
                                    alt="فرم اسکن‌شده بازدید"
                                    sx={{
                                      display: "block",
                                      maxWidth: "100%",
                                      maxHeight: 360,
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: "divider",
                                      mb: 1,
                                      objectFit: "contain",
                                      bgcolor: "action.hover",
                                    }}
                                  />
                                ) : null}
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={attachmentBusy}
                                    onClick={() =>
                                      handleOpenAttachment(fileUrl, { download: false })
                                    }
                                  >
                                    مشاهده فایل پیوست
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="text"
                                    disabled={attachmentBusy}
                                    onClick={() =>
                                      handleOpenAttachment(fileUrl, { download: true })
                                    }
                                  >
                                    دانلود
                                  </Button>
                                </Stack>
                              </Grid>
                            ) : null}

                            {formAccess.canUploadDocument ? (
                              <Grid item xs={12} md={12}>
                                <FileUploader
                                  moduleName="SLN/Supervisor"
                                  maxFiles={1}
                                  accept="image/*,application/pdf"
                                  label="فرم اسکن‌شده بازدید"
                                  onChange={(payload) => {
                                    const url = Array.isArray(payload)
                                      ? payload[0] || ""
                                      : payload || "";
                                    setFieldValue("attachedFileUrl", url);
                                  }}
                                  baseUrl={baseUrl}
                                  placeholder="فایل اسکن‌شده فرم بازدید را انتخاب کنید"
                                  {...onMakeFormikVariable("attachedFileUrl")}
                                />
                              </Grid>
                            ) : null}
                          </>
                        );
                      })()}

                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent="flex-end"
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          {isReadOnly ? "بازگشت" : "انصراف"}
                        </Button>

                        {!isReadOnly ? (
                          <Button
                            disabled={isSubmitting || loading}
                            variant="contained"
                            type="submit"
                            size="large"
                          >
                            {loading && (
                              <CircularProgress size={16} color="inherit" />
                            )}
                            {roleId !== "create" ? "ثبت گزارش بازدید" : "ایجاد"}
                          </Button>
                        ) : null}
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SiteVisitModifyPage;
